import * as React from "react";
import { Box, Typography, Button, Stack } from "@mui/material";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Presentation-layer safety net only: catches rendering errors in the React
 * tree and displays a recovery screen. It never inspects or reacts to
 * business-domain error content beyond displaying it.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <Box
          role="alert"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            gap: 2,
            textAlign: "center",
            px: 3,
          }}
        >
          <Stack spacing={2} alignItems="center">
            <Typography variant="h5">Something went wrong</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
              {this.state.error.message}
            </Typography>
            <Button variant="contained" onClick={this.handleReset}>
              Try again
            </Button>
          </Stack>
        </Box>
      );
    }
    return this.props.children;
  }
}
